/**
 * Analytics Utility
 * Handles page view and event tracking
 * Supports Google Analytics 4 (GA4)
 */

// Initialize Google Analytics
export const initGA = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') {
    return false;
  }

  // Skip in test environments
  if (process.env.NODE_ENV === 'test' || (window.location.hostname === 'localhost' && process.env.CI)) {
    return false;
  }

  try {
    // Load gtag script with error handling
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script1.onerror = () => {
      console.warn('Failed to load Google Analytics script');
    };
    document.head.appendChild(script1);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', measurementId, {
      page_path: window.location.pathname,
    });

    return true;
  } catch (error) {
    console.warn('Analytics initialization failed:', error);
    return false;
  }
};

// Track page view
export const trackPageView = (path) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  try {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
      page_path: path || window.location.pathname,
    });
  } catch (error) {
    console.warn('Analytics page view tracking failed:', error);
  }
};

// Track custom event
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  try {
    window.gtag('event', eventName, eventParams);
  } catch (error) {
    console.warn('Analytics event tracking failed:', error);
  }
};

// Common event tracking functions
export const analytics = {
  // Page navigation
  pageView: (path) => trackPageView(path),

  // User actions
  login: (method = 'email') => {
    trackEvent('login', { method });
  },
  logout: () => {
    trackEvent('logout');
  },
  signup: () => {
    trackEvent('sign_up');
  },

  // Content interactions
  viewBlogPost: (postId, postTitle) => {
    trackEvent('view_item', {
      item_id: postId,
      item_name: postTitle,
      item_category: 'blog_post',
    });
  },
  viewPortfolioItem: (itemId, itemTitle) => {
    trackEvent('view_item', {
      item_id: itemId,
      item_name: itemTitle,
      item_category: 'portfolio',
    });
  },
  search: (searchTerm, resultsCount) => {
    trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
    });
  },

  // Form submissions
  contactFormSubmit: () => {
    trackEvent('generate_lead', {
      event_category: 'contact',
    });
  },
  commentSubmit: (contentType, contentId) => {
    trackEvent('comment', {
      content_type: contentType,
      content_id: contentId,
    });
  },

  // Button clicks
  buttonClick: (buttonName, location) => {
    trackEvent('button_click', {
      button_name: buttonName,
      location,
    });
  },

  // Downloads
  download: (fileName, fileType) => {
    trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType,
    });
  },

  // Social interactions
  share: (platform, contentType, contentId) => {
    trackEvent('share', {
      method: platform,
      content_type: contentType,
      content_id: contentId,
    });
  },

  // Video/Audio interactions
  videoPlay: (videoTitle) => {
    trackEvent('video_start', {
      video_title: videoTitle,
    });
  },
  videoComplete: (videoTitle) => {
    trackEvent('video_complete', {
      video_title: videoTitle,
    });
  },
};

export default analytics;

